// src/features/stock-ranking/components/SectorRadar.tsx
//
// Sector factor radar. Each spoke is a GICS sector; the three overlaid
// series are the sector-average Momentum / Quality / Sentiment z-scores
// returned by GET /api/v1/ranking/sector-summary. This lets a user see,
// at a glance, which factor is "lit up" in which corner of the market
// (e.g. Energy strong on Quality, Tech strong on Momentum). Built on
// Recharts to match the macro-regime radar widgets.

import {
    Legend,
    PolarAngleAxis,
    PolarGrid,
    PolarRadiusAxis,
    Radar,
    RadarChart,
    ResponsiveContainer,
    Tooltip,
} from 'recharts';

import type { SectorSummaryRow } from '../api';

interface SectorRadarProps {
    sectors: SectorSummaryRow[];
}

// Recharts wants one object per spoke with a key per series.
interface RadarDatum {
    sector: string;
    Momentum: number;
    Quality: number;
    Sentiment: number;
}

// Sectors can have long GICS names; shorten for the angle-axis labels so
// the radar stays legible. Falls back to the raw name when unmapped.
const shorten = (sector: string): string => {
    const map: Record<string, string> = {
        'Information Technology': 'Info Tech',
        'Communication Services': 'Comms',
        'Consumer Discretionary': 'Cons. Disc.',
        'Consumer Staples': 'Cons. Stpl.',
        'Health Care': 'Health',
        'Financial Services': 'Financials',
        Financials: 'Financials',
        Industrials: 'Industry',
        'Real Estate': 'Real Est.',
        Materials: 'Materials',
        Utilities: 'Utilities',
        Energy: 'Energy',
    };
    return map[sector] ?? sector;
};

const RadarTooltip = ({
    active,
    payload,
    label,
}: {
    active?: boolean;
    payload?: { name: string; value: number; color: string }[];
    label?: string;
}) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-xs shadow-lg">
            <p className="font-bold text-slate-900 dark:text-white">{label}</p>
            {payload.map((p) => (
                <p key={p.name} style={{ color: p.color }}>
                    {p.name}: <span className="font-mono">{p.value.toFixed(2)}</span>
                </p>
            ))}
        </div>
    );
};

export const SectorRadar = ({ sectors }: SectorRadarProps) => {
    if (!sectors || sectors.length === 0) {
        return (
            <p className="muted text-sm">
                No sector aggregates available yet.
            </p>
        );
    }

    const data: RadarDatum[] = sectors.map((s) => ({
        sector: shorten(s.sector),
        Momentum: Number(s.avg_momentum_z.toFixed(3)),
        Quality: Number(s.avg_fundamental_z.toFixed(3)),
        Sentiment: Number(s.avg_sentiment_z.toFixed(3)),
    }));

    return (
        <div
            className="w-full h-96 text-slate-500 dark:text-slate-400"
            role="img"
            aria-label="Average factor z-scores by GICS sector"
        >
            <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={data} outerRadius="72%">
                    <PolarGrid className="stroke-slate-200 dark:stroke-slate-700" />
                    <PolarAngleAxis
                        dataKey="sector"
                        tick={{ fontSize: 11, fill: 'currentColor' }}
                    />
                    <PolarRadiusAxis
                        angle={90}
                        tick={{ fontSize: 10, fill: 'currentColor' }}
                    />
                    <Radar
                        name="Momentum"
                        dataKey="Momentum"
                        stroke="#10b981"
                        fill="#10b981"
                        fillOpacity={0.15}
                    />
                    <Radar
                        name="Quality"
                        dataKey="Quality"
                        stroke="#0ea5e9"
                        fill="#0ea5e9"
                        fillOpacity={0.15}
                    />
                    <Radar
                        name="Sentiment"
                        dataKey="Sentiment"
                        stroke="#f59e0b"
                        fill="#f59e0b"
                        fillOpacity={0.15}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Tooltip content={<RadarTooltip />} />
                </RadarChart>
            </ResponsiveContainer>
        </div>
    );
};

export default SectorRadar;
